import type { NeighborElevationCollection } from "../../../../rx/memo/useNodeNeighborBaseFloorElevationReactiveMap"
import calculateNodeVertexElevation from "./calculateNodeVertexElevation"
import getNeighborWeightsV1Cached from "./getNeighborWeightsV1Cached"

/**
 * Calculates the interpolated Z-elevation at any point within a terrain tile.
 * 
 * This is a core terrain system function that provides smooth elevation transitions
 * between neighboring tiles by using weighted interpolation based on distance from
 * tile boundaries. The function is heavily used throughout the 3D terrain rendering
 * pipeline for positioning objects, generating meshes, and creating detail props.
 * 
 * ## Usage Patterns:
 * 
 * ### 1. Terrain Mesh Generation
 * Used in `createTerrainMesh1V2Group.ts` to calculate Z-coordinates for each vertex
 * in the terrain mesh, ensuring smooth transitions between neighboring tiles.
 * 
 * ### 2. Object Positioning
 * Used in `addRandomTileDetailPropPosition.ts` to position detail props (grass, rocks, etc.)
 * at the correct elevation on the terrain surface.
 * 
 * ### 3. Boulder and Grass Wave Placement
 * Used in boulder and grass wave generation systems to ensure objects are properly
 * positioned on the terrain surface with correct elevation.
 * 
 * ## Coordinate System:
 * - Input coordinates (x, y) are in tile-local space, typically ranging from
 *   -halfSize to +halfSize (e.g., -32 to +32 for a 64-vertex tile)
 * - Coordinates are normalized to [-1, 1] range for weight calculation
 * - Output Z-coordinate is in world space elevation units
 * 
 * ## Performance Characteristics:
 * - Uses cached weight calculation (`getNeighborWeightsV1Cached`) to avoid
 *   redundant computations for frequently accessed coordinates
 * - Optimized for batch processing during terrain mesh generation
 * - Minimal memory allocation - reuses cached weight objects
 * 
 * @param baseElevationsCollection - Collection containing base elevation and 8 neighbor elevations
 *   with pre-computed averages for smooth interpolation. This comes from the reactive
 *   elevation system that tracks terrain changes.
 * @param nx - X-normalized coordinate in tile-local space (typically -1 to +1)
 * @param ny - Y-normalized coordinate in tile-local space (typically -1 to +1)
 * 
 * @returns Interpolated elevation at the specified point, accounting for influence
 *   from neighboring tiles for seamless terrain transitions
 * 
 * @example
 * ```typescript
 * // Get elevation for positioning a detail prop
 * const elevation = getTerrainMesh1Z(baseElevationsCollection, 10, -5)
 * detailProp.position.set(worldX, worldY, elevation)
 * 
 * // Generate terrain mesh vertices
 * for (let i = 0; i < vertexCount; i++) {
 *   const x = positionAttr.getX(i)
 *   const y = positionAttr.getY(i)
 *   const z = getTerrainMesh1Z(baseElevationsCollection, x, y)
 *   positionAttr.setZ(i, z)
 * }
 * ```
 * 
 * @see {@link calculateNodeVertexElevation} - Performs the actual weighted interpolation
 * @see {@link getNeighborWeightsV1Cached} - Provides cached distance-based weights
 * @see {@link NeighborElevationCollection} - Data structure containing elevation data
 */
export default function getTerrainMesh1FromNormalizedTileRXZ(
  baseElevationsCollection: NeighborElevationCollection,
  nx: number,
  ny: number
): number {
  // Normalize coordinates to [-1, 1] range for weight calculation

  // Get cached weights based on distance from tile boundaries
  const vertexWeights = getNeighborWeightsV1Cached(nx, ny)
  // const vertexWeights = createNeighborWeightsV1(nx, ny)

  // Calculate weighted elevation using neighbor influence
  const vertexElevation = calculateNodeVertexElevation(vertexWeights, baseElevationsCollection)

  return vertexElevation
}